import { useState } from 'react';
import { usePlatform } from '@localagent/platform';
import { Button, Input } from '@localagent/ui';
import { toast } from '@localagent/ui/components/sonner';
import { uuidV4, type WorkspaceProjection } from '@localagent/sdk';
import { useReassignWorkstation } from '../../hooks/useWorkspaces';
import { useDeviceId } from '../../services/sdk-instance';

type MoveMode = 'change-folder' | 'move-from-offline' | 'move-from-online';

export function canMoveWorkspaceHere(projection: WorkspaceProjection): boolean {
  return projection.assignment.routing_class === 'local' && projection.lifecycle === 'active';
}

function moveMode(projection: WorkspaceProjection, thisDeviceId: string): MoveMode {
  if (projection.workstation.device_id === thisDeviceId) return 'change-folder';
  return projection.workstation.status === 'connected' ? 'move-from-online' : 'move-from-offline';
}

const COPY: Record<MoveMode, { intro: string; open: string; submit: string; done: string }> = {
  'change-folder': {
    intro: 'Use a different folder on this computer for this workspace.',
    open: 'Change folder…',
    submit: 'Use this folder',
    done: 'Workspace folder changed.',
  },
  'move-from-offline': {
    intro:
      'This workspace lives on a computer that is not connected. You can move it to this computer and pick the folder it should use here.',
    open: 'Move to this computer…',
    submit: 'Move to this computer',
    done: 'Workspace moved to this computer.',
  },
  'move-from-online': {
    intro:
      'This workspace is set up on another computer. Moving it here means the other computer stops using its folder for this workspace.',
    open: 'Move to this computer…',
    submit: 'Move to this computer',
    done: 'Workspace moved to this computer.',
  },
};

export function WorkspaceMoveControl({ projection }: { projection: WorkspaceProjection }) {
  const platform = usePlatform();
  const thisDeviceId = useDeviceId();
  if (platform.type !== 'desktop' || thisDeviceId === null) return null;
  return <WorkspaceMoveControlView projection={projection} thisDeviceId={thisDeviceId} />;
}

// Reattaching only on an explicit choice of folder; a matching path elsewhere is a different folder (workspace_state ADR-028).
export function WorkspaceMoveControlView({
  projection,
  thisDeviceId,
}: {
  projection: WorkspaceProjection;
  thisDeviceId: string;
}) {
  const platform = usePlatform();
  const reassign = useReassignWorkstation(projection.workspace_id);
  const mode = moveMode(projection, thisDeviceId);
  const copy = COPY[mode];
  const currentFolder = mode === 'change-folder' ? (projection.binding?.folder_path ?? '') : '';
  const [open, setOpen] = useState(mode === 'move-from-offline');
  const [folderPath, setFolderPath] = useState(currentFolder);
  const [error, setError] = useState<string | null>(null);

  if (!canMoveWorkspaceHere(projection)) return null;

  const availability = projection.actions.reassign_workstation;
  if (!availability.available) {
    return (
      <p className="mt-3 text-caption text-muted-foreground" data-testid="workspace-move-unavailable">
        {availability.refusal.message}
      </p>
    );
  }

  const trimmed = folderPath.trim();
  const unchanged = mode === 'change-folder' && trimmed === currentFolder;

  const browse = async () => {
    if (typeof platform.selectDirectory !== 'function') return;
    const picked = await platform.selectDirectory();
    if (picked) setFolderPath(picked);
  };

  const submit = async () => {
    setError(null);
    try {
      await reassign.mutateAsync({ folderPath: trimmed, idempotencyKey: uuidV4() });
      toast.success(copy.done);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (!open) {
    return (
      <div className="mt-3" data-testid="workspace-move" data-mode={mode}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="workspace-move-open"
        >
          {copy.open}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-md bg-warning-subtle p-3"
      data-testid="workspace-move"
      data-mode={mode}
    >
      <p className="text-body">{copy.intro}</p>
      <div className="mt-2 flex gap-2">
        <Input
          value={folderPath}
          onChange={(e) => setFolderPath(e.target.value)}
          placeholder="/path/to/folder on this computer"
          aria-label="Folder on this computer"
          data-testid="workspace-move-path"
        />
        {typeof platform.selectDirectory === 'function' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void browse()}
            disabled={reassign.isPending}
            data-testid="workspace-move-browse"
          >
            Browse…
          </Button>
        )}
      </div>
      {error !== null && (
        <p className="mt-2 text-caption text-destructive" role="alert" data-testid="workspace-move-error">
          {error}
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          disabled={reassign.isPending || trimmed === '' || unchanged}
          onClick={() => void submit()}
          data-testid="workspace-move-submit"
        >
          {reassign.isPending ? 'Moving…' : copy.submit}
        </Button>
        {mode !== 'move-from-offline' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setOpen(false);
              setFolderPath(currentFolder);
              setError(null);
            }}
            disabled={reassign.isPending}
            data-testid="workspace-move-cancel"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
